import { useEffect, useRef, useState } from 'react';

const useTruncatedWithCount = (formattedItems: string[], maxLines = 2) => {
  const [visibleText, setVisibleText] = useState('');
  const [hiddenCount, setHiddenCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || formattedItems.length === 0) {
      setVisibleText(formattedItems.join('; '));
      setHiddenCount(0);
      return;
    }

    const checkFit = () => {
      const container = containerRef.current;
      if (!container) return;

      const lineHeight = parseFloat(getComputedStyle(container).lineHeight);
      const maxHeight = lineHeight * maxLines;

      // Create a temporary measuring element
      const measure = document.createElement('div');
      const containerStyles = window.getComputedStyle(container);
      measure.style.position = 'absolute';
      measure.style.visibility = 'hidden';
      measure.style.width = `${container.clientWidth}px`;
      measure.style.fontSize = containerStyles.fontSize;
      measure.style.fontFamily = containerStyles.fontFamily;
      measure.style.lineHeight = containerStyles.lineHeight;
      measure.style.whiteSpace = 'normal';
      measure.style.wordWrap = 'break-word';
      document.body.appendChild(measure);

      // Try full text first
      const fullText = formattedItems.join('; ');
      measure.textContent = fullText;

      if (measure.scrollHeight <= maxHeight) {
        // All items fit
        setVisibleText(fullText);
        setHiddenCount(0);
        document.body.removeChild(measure);
        return;
      }

      // Binary search for the best fit
      let left = 0;
      let right = formattedItems.length;
      let bestFit = 0;

      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const remaining = formattedItems.length - mid;

        if (mid === 0) {
          break;
        }

        const testText =
          formattedItems.slice(0, mid).join('; ') +
          (remaining > 0 ? ` ... +${remaining} more` : '');

        measure.textContent = testText;

        if (measure.scrollHeight <= maxHeight) {
          bestFit = mid;
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }

      document.body.removeChild(measure);

      if (bestFit < formattedItems.length) {
        setVisibleText(formattedItems.slice(0, bestFit).join('; '));
        setHiddenCount(formattedItems.length - bestFit);
      } else {
        setVisibleText(fullText);
        setHiddenCount(0);
      }
    };

    // Wait for container to be fully rendered
    const timer = setTimeout(checkFit, 10);
    return () => clearTimeout(timer);
  }, [formattedItems]);

  return {
    visibleText,
    hiddenCount,
    containerRef,
  };
};

export default useTruncatedWithCount;
